import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiActivity,
  FiBarChart2,
  FiChevronDown,
  FiChevronUp,
  FiCreditCard,
  FiDownload,
  FiFileText,
  FiHome,
  FiLock,
  FiLogOut,
  FiSettings,
  FiShield,
} from 'react-icons/fi';
import { VscGraphLeft } from 'react-icons/vsc';
import { IoGameControllerOutline } from 'react-icons/io5';
import { MdAccountBalance, MdCardGiftcard } from 'react-icons/md';
import { RiAdminLine } from 'react-icons/ri';

import { cn, showToastMessage, useRoleIds } from '@/utils';
import { NavItem, NavItemComponentProps, UserRole } from '@/types';

import { useLogoutMutation } from '@/store/api/authApi';
import { useDispatch, useSelector } from 'react-redux';
import { logout as clearAuth, RootState } from '@/store';
import { clearUser } from '@/store/slices/userSlice';
import apiSlice from '@/store/api/apiSlice';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { usePermissions } from '@/context/PermissionContext';
const isMobile = window.innerWidth < 768;
interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const DOWNLINE_NAVIGATION_ITEM_NAMES = new Set([
  'Dashboard',
  'Downline',
  'Risk Management',
  'My Reports',
  'Account Statement',
  'Export Report History',
  'Change Password',
  'Activity Log',
]);

type HasModuleAccess = ReturnType<typeof usePermissions>['hasModuleAccess'];

function canShowDownlineNavigationItem(item: NavItem) {
  if (item.onlyBanker) return false;

  return DOWNLINE_NAVIGATION_ITEM_NAMES.has(item.name);
}

function canShowNavigationItem(
  item: NavItem,
  {
    isOwner,
    hasModuleAccess,
  }: {
    isOwner: boolean;
    hasModuleAccess: HasModuleAccess;
  }
) {
  if (item.onlyOwner) return false;
  if (item.onlyBanker) return false;
  if (!item.permissionKey) return true;
  if (isOwner) return true;

  return hasModuleAccess(item.permissionKey, 'read');
}

function SidebarIcon({
  icon: Icon,
  iconClassName,
  isOpen,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  isOpen: boolean;
}) {
  if (!Icon) return null;

  return (
    <span
      className={cn(
        'grid size-[26px] shrink-0 place-items-center rounded-md',
        isOpen && 'mr-3',
        iconClassName
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
    </span>
  );
}

function NavItemComponent({
  item,
  isOpen,
  level = 0,
  activeMenu,
  setActiveMenu,
  onToggle,
  iconClassName,
}: NavItemComponentProps) {
  const isExpanded = activeMenu === item.name;
  const location = useLocation();

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  useEffect(() => {
    if (hasActiveChild && isOpen) {
      setActiveMenu(item.name);
    }
  }, [hasActiveChild, isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      setActiveMenu(isExpanded ? null : item.name);
    }
  };
  const Icon = item.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={cn(
            'flex min-h-10 w-full items-center rounded-lg px-3 py-2 text-base font-medium transition-colors',
            isActive || hasActiveChild
              ? 'bg-secondary text-white'
              : 'text-gray hover:bg-accent hover:text-accent-foreground',
            !isOpen && 'justify-center px-0'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
          title={!isOpen ? item.name : undefined}
        >
          <SidebarIcon icon={Icon} isOpen={isOpen} iconClassName={iconClassName} />

          {isOpen && (
            <>
              <span className="flex-1 text-left text-xs sm:text-sm">{item.name}</span>
              {isExpanded ? (
                <FiChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <FiChevronDown className="ml-2 h-4 w-4" />
              )}
            </>
          )}
        </button>

        {/* Tree Menu */}
        {isExpanded && isOpen && (
          <div className=" ml-6">
            <div className="relative border-l-2 border-gray  space-y-4 py-2">
              {item.children!.map((child) => (
                <div key={child.name} className="relative">
                  {/* tick */}
                  <span className="absolute -left-0.5 top-2 h-0.5 w-4 bg-gray-600"></span>

                  {child.children ? (
                    <NavItemComponent
                      item={child}
                      isOpen={isOpen}
                      level={level + 1}
                      activeMenu={activeMenu}
                      setActiveMenu={setActiveMenu}
                      onToggle={onToggle}
                      iconClassName={child.iconClassName}
                    />
                  ) : (
                    <NavLink
                      onClick={() => {
                        if (isMobile) {
                          onToggle?.();
                        }
                      }}
                      to={child.href!}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center text-xs sm:text-sm transition-colors',
                          isActive ? 'text-secondary font-semibold' : 'text-gray hover:text-blue'
                        )
                      }
                    >
                      - {child.name}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href!}
      onClick={() => {
        if (isMobile) {
          onToggle?.();
        }
      }}
      className={({ isActive }) =>
        cn(
          'flex min-h-10 items-center rounded-lg px-2 py-1.5 sm:py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-secondary text-white border-l-2'
            : 'text-gray hover:bg-accent hover:text-accent-foreground',
          !isOpen && 'justify-center px-0'
        )
      }
      style={{ paddingLeft: isOpen ? `${10 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      <SidebarIcon icon={Icon} isOpen={isOpen} iconClassName={iconClassName} />

      {isOpen && <span className="text-xs sm:text-sm">{item.name}</span>}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const [logout, { isLoading: logoutLoading }] = useLogoutMutation();
  const dispatch = useDispatch();

  const { hasModuleAccess } = usePermissions();
  const { isOwner, isDownlineUser, isRoleUser } = useRoleCheck();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const { superAdminID, adminID, superMasterId, subSuperMasterId, masterId, userId } = useRoleIds();
  const userRole = user?.data?.role?.name as UserRole | undefined;

  const DOWNLINE_ROLE_VISIBILITY: Record<UserRole, UserRole[]> = {
    OWNER: ['ADMIN', 'SUPER MASTER', 'SUB SUPER MASTER', 'MASTER', 'USER'],
    'SUPER ADMIN': ['ADMIN', 'USER'],
    ADMIN: ['SUPER MASTER', 'SUB SUPER MASTER', 'MASTER', 'USER'],
    'SUPER MASTER': ['SUB SUPER MASTER', 'MASTER', 'USER'],
    'SUB SUPER MASTER': ['MASTER', 'USER'],
    MASTER: ['USER'],
    USER: [],
  };

  const effectiveRole = isRoleUser ? 'OWNER' : userRole;
  const visibleRoles: UserRole[] =
    effectiveRole && effectiveRole in DOWNLINE_ROLE_VISIBILITY
      ? DOWNLINE_ROLE_VISIBILITY[effectiveRole]
      : [];

  const downlineChildren: NavItem[] = [
    { name: 'Super Admin', href: `/members/${superAdminID}`, role: 'SUPER ADMIN' },
    { name: 'Admin', href: `/members/${adminID}`, role: 'ADMIN' },
    // { name: 'Sub Admin', href: `/members/${subAdminId}`, role: 'SUB ADMIN' },
    { name: 'Super Master', href: `/members/${superMasterId}`, role: 'SUPER MASTER' },
    // { name: 'Sub Super Master', href: `/members/${subSuperMasterId}`, role: 'SUB SUPER MASTER' },
    { name: 'Master', href: `/members/${masterId}`, role: 'MASTER' },
    { name: 'User', href: `/members/${userId}`, role: 'USER' },
    { name: 'Settlement', href: `/settlement`, role: 'USER' },
  ].filter((item) => visibleRoles.includes(item.role as UserRole));

  const navigation: NavItem[] = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: FiHome,
      permissionKey: 'Dashboard',
    },
    {
      name: 'Downline',
      icon: VscGraphLeft,
      iconClassName: 'rotate-90',
      permissionKey: 'Users',
      children: downlineChildren,
    },
    {
      name: 'Risk Management',
      href: '/risk',
      icon: FiShield,
      permissionKey: 'RiskManagement',
    },
    {
      name: 'My Reports',
      icon: FiFileText,
      permissionKey: 'MyReport',
      children: [
        { name: 'Bet List', href: '/bet-list' },
        { name: 'Casino Bet List', href: '/casino-bet-list' },
        { name: 'P/L Report By Downline', href: '/pl-by-downline' },
        { name: 'P/L Report By Event', href: '/pl-by-event' },
        { name: 'P/L Report By Player', href: '/pl-by-player' },
        { name: 'P/L Report By Casino Downline', href: '/casinopl-by-downline' },
        { name: 'P/L Report By Casino Players', href: '/casinopl-by-player' },
        { name: 'Business Report', href: '/business-reports' },
        // {
        //   name: 'Inactive Members',
        //   href: '/inactive-members',
        // },
      ],
    },

    {
      name: 'Event Management',
      icon: FiCreditCard,
      permissionKey: 'EventManagement',
      children: [
        { name: 'Events', href: '/event-management' },
        { name: 'Bet Config', href: '/bet-setting' },
      ],
    },

    {
      name: 'Casino Control',
      icon: IoGameControllerOutline,
      href: '/casino-management',
      permissionKey: 'CasinoControl',
    },

    {
      name: 'Content Management',
      href: '/content-management',
      icon: FiSettings,
      permissionKey: 'ContentManagement',
    },

    {
      name: 'Bonus Management',
      icon: MdCardGiftcard,
      permissionKey: 'Bonus',
      children: [
        {
          name: 'Policy Management',
          href: '/bonus/policy-management',
        },
        {
          name: 'Bonus Statement',
          href: '/bonus/statement',
        },
        {
          name: 'Referral Commission',
          href: '/bonus/referral-commission',
        },
      ],
    },
    {
      name: 'Banking Management',
      icon: MdAccountBalance,
      permissionKey: 'BankingManagement',
      children: [
        {
          name: 'Deposit/Withdraw Request',
          href: '/requests/Credit/all',
        },
        {
          name: 'Manage Accounts',
          href: '/manage-account',
        },
        {
          name: 'Telegram Approvers',
          href: '/telegram-approvers',
        },
        {
          name: 'Crypto Conversion Settings',
          href: '/crypto-conversion',
        },
      ],
    },
    {
      name: 'Roles And Privileges',
      icon: RiAdminLine,
      href: '/roles-and-privileges',
      permissionKey: 'Roles Privileges',
    },

    {
      name: 'Account Statement',
      icon: FiFileText,
      href: '/rolewise-account-statement',
      permissionKey: 'Account Statement',
    },
    {
      name: 'Export Report History',
      icon: FiDownload,
      href: '/export',
      permissionKey: 'Export History',
    },
    {
      name: 'Change Password',
      icon: FiLock,
      href: '/settings',
    },
    {
      name: 'Activity Log',
      icon: FiActivity,
      href: '/activity-logs',
      permissionKey: 'Activity Logs',
    },
  ];
  const filteredNavigation = React.useMemo(() => {
    if (isDownlineUser) {
      return navigation.filter(canShowDownlineNavigationItem);
    }

    return navigation.filter((item) =>
      canShowNavigationItem(item, {
        isOwner,
        hasModuleAccess,
      })
    );
  }, [navigation, isDownlineUser, isOwner, hasModuleAccess]);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      apiSlice.util.resetApiState();
      showToastMessage('success', 'You have been logged out successfully.');
      navigate('/login');
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Failed to logout. Please try again.');
    }
  };
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-20 bg-sidebar/20   lg:hidden" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col text-gray bg-sidebar transition-all duration-300 overflow-hidden  border-r border-r-green3',
          isOpen ? 'w-72 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div
          className={cn(
            'flex h-[65px] items-center bg-secondary',
            isOpen ? 'px-4' : 'justify-center px-2'
          )}
        >
          <div className={cn('flex w-full', !isOpen && 'justify-center')}>
            <img className={cn('h-auto', isOpen ? 'w-[142px]' : 'w-10')} src="/images/logo-9winbet.webp" />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {filteredNavigation?.map((item) => (
            <NavItemComponent
              key={item.name}
              iconClassName={item.iconClassName}
              item={item}
              isOpen={isOpen}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              onToggle={onToggle}
            />
          ))}
        </nav>

        {/* Logout */}

        <div
          onClick={handleLogout}
          className={cn(
            'flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray border-t border-t-green3 py-2 sm:py-5 px-2',
            isOpen ? 'gap-3' : 'justify-center'
          )}
          title={!isOpen ? 'Logout' : undefined}
        >
          <FiLogOut className="h-5 w-5 shrink-0" />
          {isOpen && 'Logout'}
        </div>
      </div>
    </>
  );
}
